import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useSubscription } from "../hooks/useSubscription";

export default function ImportLinkModal({ open, onClose, onImported }) {
  const sub = useSubscription();
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null); // { kind: "track" | "collection", ... }
  const [selected, setSelected] = useState({}); // apple_track_id -> boolean
  const [importing, setImporting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setUrl("");
    setPreview(null);
    setSelected({});
  };

  const onScan = async () => {
    if (!sub.canImport) {
      window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: "import_link" } }));
      return;
    }
    if (!url.trim()) return;
    setScanning(true);
    try {
      const r = await api.post("/import/scan", { url: url.trim() });
      const data = r.data;
      setPreview(data);
      if (data.kind === "collection") {
        // Select all tracks by default
        const init = {};
        (data.tracks || []).forEach((t) => { if (t.apple_track_id) init[t.apple_track_id] = true; });
        setSelected(init);
        if (!data.tracks?.length) toast("Album lookup returned no tracks", { duration: 4000 });
      } else if (!data.title) {
        toast("Could not auto-detect — please fill in details before saving", { duration: 4000 });
      }
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        onClose?.();
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "Could not fetch metadata");
      }
    } finally {
      setScanning(false);
    }
  };

  const onConfirmTrack = async () => {
    if (!preview?.title) {
      toast.error("Title is required");
      return;
    }
    try {
      const r = await api.post("/tracks", {
        title: preview.title,
        primary_artist: preview.primary_artist,
        isrc: preview.isrc,
        duration: preview.duration,
        release_date: preview.release_date || undefined,
        genre: preview.genre,
        artwork_url: preview.artwork_url,
        source_url: preview.source_url,
        source_platform: preview.source_platform,
        external_ids: preview.external_ids,
        status: preview.release_date ? "released" : "idea",
      });
      onImported?.(r.data);
      toast.success("Track imported");
      reset();
      onClose?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        onClose?.();
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "Import failed");
      }
    }
  };

  const onConfirmAlbum = async () => {
    if (!preview?.collection || !preview.tracks) return;
    const chosen = preview.tracks.filter((t) => selected[t.apple_track_id]);
    if (chosen.length === 0) {
      toast.error("Select at least one track");
      return;
    }
    setImporting(true);
    try {
      const c = preview.collection;
      const r = await api.post("/import/album", {
        collection_id: c.collection_id,
        title: c.title,
        primary_artist: c.primary_artist,
        type: c.type,
        release_date: c.release_date || "",
        cover_art_url: c.cover_art_url || "",
        source_url: c.source_url || "",
        tracks: chosen,
      });
      onImported?.(r.data.project);
      const { imported = 0, updated = 0 } = r.data;
      toast.success(
        `Album added — ${imported} new${updated ? `, ${updated} updated` : ""}`
      );
      reset();
      onClose?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        onClose?.();
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "Album import failed");
      }
    } finally {
      setImporting(false);
    }
  };

  const onPaste = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt) setUrl(txt);
    } catch { /* ignore */ }
  };

  const toggleAll = (on) => {
    if (!preview?.tracks) return;
    const next = {};
    preview.tracks.forEach((t) => { if (t.apple_track_id) next[t.apple_track_id] = on; });
    setSelected(next);
  };

  const isCollection = preview?.kind === "collection";
  const selectedCount = isCollection
    ? preview.tracks.filter((t) => selected[t.apple_track_id]).length
    : 0;

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="import-link-modal">
      <div
        className="modal-card"
        style={{ maxWidth: isCollection ? 640 : 520, padding: "28px 24px", maxHeight: "88vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow" style={{ color: "var(--accent)" }}>— Import Link</div>
        <h3 className="serif" style={{ fontSize: 30, lineHeight: 1.05, margin: "8px 0 6px" }}>
          Paste a streaming URL
        </h3>
        <p style={{ color: "var(--text-mute)", fontSize: 13, marginBottom: 18 }}>
          Apple Music track or album, Tidal, YouTube, or YouTube Music. Backend pulls full metadata.
        </p>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="import-url-input"
            className="lq-input"
            placeholder="https://music.apple.com/us/album/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button className="btn btn-light" onClick={onPaste} data-testid="paste-btn">Paste</button>
        </div>

        {!preview && (
          <button
            className="btn btn-primary btn-block"
            onClick={onScan}
            disabled={scanning || !url.trim()}
            style={{ marginTop: 12 }}
            data-testid="scan-btn"
          >
            {scanning ? "Scanning…" : "Fetch Metadata →"}
          </button>
        )}

        {/* --- Single track preview --- */}
        {preview && !isCollection && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: "var(--bg-elev-2)",
              borderRadius: 4,
              border: "1px solid var(--border-soft)",
            }}
            data-testid="track-preview"
          >
            <div className="eyebrow" style={{ color: "var(--accent)" }}>— Preview · Track</div>
            <div
              className="serif"
              style={{ fontSize: 24, lineHeight: 1.1, margin: "8px 0 4px" }}
              data-testid="preview-title"
            >
              {preview.title || "(no title detected)"}
            </div>
            <div style={{ fontSize: 14, color: "var(--text-mute)" }}>
              {preview.primary_artist || "—"}
            </div>
            <div className="data-line" style={{ marginTop: 10 }}>
              {preview.isrc ? `ISRC ${preview.isrc} · ` : ""}
              {preview.duration ? `${preview.duration} · ` : ""}
              {preview.release_date ? `${preview.release_date} · ` : ""}
              {preview.genre || preview.album || ""}
            </div>

            {(!preview.title || !preview.primary_artist) && (
              <div style={{ marginTop: 14 }}>
                <div className="eyebrow" style={{ marginBottom: 8 }}>— Confirm details</div>
                <input
                  className="lq-input"
                  placeholder="Title"
                  value={preview.title || ""}
                  onChange={(e) => setPreview({ ...preview, title: e.target.value })}
                />
                <input
                  className="lq-input"
                  placeholder="Artist"
                  value={preview.primary_artist || ""}
                  onChange={(e) => setPreview({ ...preview, primary_artist: e.target.value })}
                  style={{ marginTop: 8 }}
                />
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={reset} data-testid="rescan-btn">← Re-scan</button>
              <button
                className="btn btn-primary"
                onClick={onConfirmTrack}
                data-testid="confirm-import-btn"
                style={{ flex: 1 }}
              >
                Add to Catalog →
              </button>
            </div>
          </div>
        )}

        {/* --- Album/EP preview --- */}
        {preview && isCollection && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              background: "var(--bg-elev-2)",
              borderRadius: 4,
              border: "1px solid var(--border-soft)",
            }}
            data-testid="album-preview"
          >
            <div className="eyebrow" style={{ color: "var(--accent)" }}>
              — Preview · {(preview.collection.type || "album").toUpperCase()}
            </div>

            <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
              {preview.collection.cover_art_url ? (
                <img
                  src={preview.collection.cover_art_url}
                  alt={preview.collection.title}
                  data-testid="album-cover"
                  style={{
                    width: 96, height: 96, objectFit: "cover",
                    borderRadius: 4, border: "1px solid var(--border-soft)",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: 96, height: 96, background: "var(--bg-elev-3)",
                  borderRadius: 4, border: "1px solid var(--border-soft)", flexShrink: 0,
                }} />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  className="serif"
                  style={{ fontSize: 22, lineHeight: 1.15, margin: "0 0 4px", wordBreak: "break-word" }}
                  data-testid="album-title"
                >
                  {preview.collection.title || "(untitled release)"}
                </div>
                <div style={{ fontSize: 14, color: "var(--text-mute)" }} data-testid="album-artist">
                  {preview.collection.primary_artist || "—"}
                </div>
                <div className="data-line" style={{ marginTop: 8 }}>
                  {preview.collection.release_date ? `${preview.collection.release_date} · ` : ""}
                  {preview.tracks.length} track{preview.tracks.length === 1 ? "" : "s"}
                  {preview.collection.genre ? ` · ${preview.collection.genre}` : ""}
                </div>
              </div>
            </div>

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 16, marginBottom: 6, gap: 8,
            }}>
              <div className="eyebrow">— Tracks ({selectedCount}/{preview.tracks.length})</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 11 }}
                  onClick={() => toggleAll(true)}
                  data-testid="select-all-tracks"
                >All</button>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 11 }}
                  onClick={() => toggleAll(false)}
                  data-testid="select-none-tracks"
                >None</button>
              </div>
            </div>

            <div style={{
              maxHeight: 280, overflow: "auto",
              border: "1px solid var(--border-soft)", borderRadius: 4,
            }}>
              {preview.tracks.map((t, idx) => {
                const id = t.apple_track_id || `idx-${idx}`;
                const checked = !!selected[t.apple_track_id];
                return (
                  <label
                    key={id}
                    data-testid={`album-track-row-${idx}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px",
                      borderBottom: idx === preview.tracks.length - 1 ? "none" : "1px solid var(--border-soft)",
                      cursor: "pointer",
                      background: checked ? "rgba(219,165,90,0.05)" : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setSelected({ ...selected, [t.apple_track_id]: e.target.checked })}
                      data-testid={`album-track-checkbox-${idx}`}
                      style={{ accentColor: "var(--accent)" }}
                    />
                    <span className="mono" style={{ width: 22, color: "var(--text-fade)", fontSize: 11 }}>
                      {String(t.track_number || idx + 1).padStart(2, "0")}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.title || "(untitled)"}
                    </span>
                    {t.isrc ? (
                      <span className="mono" style={{ fontSize: 10, color: "var(--text-fade)" }}>{t.isrc}</span>
                    ) : null}
                    {t.duration ? (
                      <span className="mono" style={{ fontSize: 11, color: "var(--text-mute)", width: 38, textAlign: "right" }}>{t.duration}</span>
                    ) : null}
                  </label>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={reset} data-testid="rescan-btn">← Re-scan</button>
              <button
                className="btn btn-primary"
                onClick={onConfirmAlbum}
                disabled={importing || selectedCount === 0}
                data-testid="confirm-album-btn"
                style={{ flex: 1 }}
              >
                {importing ? "Adding…" : `Add Album to Catalog (${selectedCount}) →`}
              </button>
            </div>
          </div>
        )}

        <button
          className="btn btn-ghost btn-block"
          onClick={() => { reset(); onClose?.(); }}
          style={{ marginTop: 14 }}
          data-testid="cancel-import-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
